import React, { useState, useEffect } from 'react';
import PasswordScreen from './PasswordScreen';
import styled from 'styled-components';
import { Link } from 'react-scroll';
import './index.css';
import './fonts.css';

// Loading Screen
const LoadingScreen = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #99b9cc;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  transition: opacity 0.5s ease-out;
  overflow: visible; /* Prevent clipping of rotated text */

  &.loaded {
    opacity: 0;
    pointer-events: none;
  }
`;

const LoadingText = styled.p`
  font-family: var(--font-taxi);
  font-size: 4rem;
  color: #feebdc;
  transform: rotate(-20deg);
  animation: pulse 1.5s infinite ease-in-out;
  padding: 1rem; /* Add padding to give breathing room */

  @keyframes pulse {
    0% { transform: rotate(-20deg) scale(1); }
    50% { transform: rotate(-20deg) scale(1.05); }
    100% { transform: rotate(-20deg) scale(1); }
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    padding: 0.5rem; /* Adjust padding for mobile */
  }
`;

// RSVP Modal
// Updated RSVP Modal with improved styling
const RsvpModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  max-width: 800px;
  height: 80vh;
  background-color: var(--tes-cream);
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 99999;
  padding: 2rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 95vw;
    height: 85vh;
    padding: 1.5rem;
  }
`;

// Updated event item using button-style approach
const EventItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const EventLabel = styled.h3`
  font-family: var(--font-wingman);
  font-size: 1.2rem;
  color: var(--dark);
  margin-bottom: 0.5rem;
`;

// Event button options with selection indicator
const EventButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: white;
  padding: 1rem;
  border-radius: 8px;
  font-family: var(--font-wingman);
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  color: var(--dark);
  border: ${props => props.selected ? '3px solid var(--tes-green)' : '1px solid #ddd'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const EventOptions = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CantAttendButton = styled.button`
  background-color: white;
  color: black;
  border: ${props => props.selected ? '3px solid var(--tes-red)' : '1px solid #ddd'};
  border-radius: 8px;
  padding: 0.8rem 1rem;
  font-family: var(--font-wingman);
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1.5rem;
  width: 100%;
  max-width: 400px;
  align-self: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const SubmitButton = styled.button`
  background-color: var(--tes-green);
  color: var(--tes-yellow);
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-family: var(--font-wingman);
  font-size: 1.2rem;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.3s ease;
  align-self: center;
  margin-top: 1.5rem;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'scale(1.05)'};
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 10px 20px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99999;
`;

const ModalTitle = styled.h2`
  font-family: var(--font-wingman);
  font-size: 2rem;
  color: var(--tes-dark-blue);
  margin-top: 3rem;
  margin-bottom: 1.5rem;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ModalForm = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const RsvpButton = styled.button`
  display: inline-block;
  cursor: pointer;
  transition: transform 0.3s ease;
  margin-top: 0rem;
  background: none;
  border: none;
  padding: 0;

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 15vw;
    max-width: 200px;
    min-width: 150px;
    height: auto;
  }

  @media (max-width: 768px) {
    img {
      width: 30vw;
      max-width: 500px;
      min-width: 120px;
    }
  }

  @media (max-width: 480px) {
    img {
      width: 50vw;
      max-width: 500px;
      min-width: 100px;
    }
  }
`;

// Existing Styled Components (unchanged)
const AppContainer = styled.div`
  text-align: center;
  overflow-x: hidden;
  width: 100%;
  max-width: 100vw;
  background-color: black;
`;

const NavBar = styled.nav`
  background-color: var(--tes-cream);
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 3rem;
  height: 60px;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
    height: 60px;
    overflow: visible;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100%;
    height: 20px;
    background-image: url('/navbar.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    z-index: 0;
  }
`;

const NavBrand = styled.div`
  flex: 0 0 100px;
  font-family: var(--font-wingman);
  font-weight: bold;
  font-size: 1.5rem;
  color: var(--dark);
  flex: 1;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const NavDate = styled.div`
  flex: 0 0 100px;
  font-family: var(--font-wingman);
  font-weight: bold;
  font-size: 1.3rem;
  color: var(--dark);
  flex: 1;
  text-align: right;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const NavCenter = styled.div`
  display: flex;
  justify-content: center;
  flex: 2;

  @media (max-width: 768px) {
    position: relative;
    z-index: 1000;
  }
`;

const NavList = styled.ul`
  display: flex;
  justify-content: center;
  text-align: center;
  flex: 1;
  list-style: none;
  padding: 0;
  margin: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: var(--tes-cream);
    border-radius: 0 0 10px 10px;
    padding: 1rem 0;
    max-height: ${(props) => (props.isOpen ? '300px' : '0')};
    overflow: hidden;
    transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
    opacity: ${(props) => (props.isOpen ? '1' : '0')};
    visibility: ${(props) => (props.isOpen ? 'visible' : 'hidden')};
    z-index: 998;
    width: 250px;
  }
`;

const NavLink = styled(Link)`
  display: inline-block;
  padding: 1rem;
  font-family: var(--font-wingman);
  font-weight: bold;
  color: var(--dark);
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: var(--tes-green);
  }

  @media (max-width: 768px) {
    display: block;
    width: 100%;
    padding: 1rem;
    font-size: 1.2rem;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
`;

const NavItem = styled.li`
  margin: 0 0.05rem;

  @media (max-width: 768px) {
    margin: 0;
    width: 100%;
    text-align: center;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--font-wingman);
  font-weight: bold;
  font-size: 1rem;
  color: var(--dark);
  padding: 0.5rem 1rem;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-size: 1.4rem;
    padding: 1rem;
  }

  &:hover {
    color: var(--accent);
  }
`;

const MenuIcon = styled.div`
  width: 20px;
  height: 2px;
  background-color: ${(props) => (props.isOpen ? 'transparent' : 'var(--dark)')};
  position: relative;
  transition: all 0.3s ease-in-out;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 2px;
    background-color: var(--dark);
    transition: all 0.3s ease-in-out;
  }

  &::before {
    transform: translateY(-6px);
    ${(props) => props.isOpen && `transform: rotate(45deg);`}
  }

  &::after {
    transform: translateY(6px);
    ${(props) => props.isOpen && `transform: rotate(-45deg);`}
  }
`;

const ScheduleCardsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
  margin: 2rem auto;
  margin-top:4rem;
  @media (max-width: 992px) {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    margin-top:2rem;

  }
`;

const ScheduleCard = styled.div`
  flex: 1;
  min-width: 280px;
  max-width: 400px;
  transition: transform 0.3s ease;

  img {
    width: 100%;
    height: auto;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 992px) {
    max-width: 80%;
  }
`;

const ScheduleDivider = styled.hr`
  width: 90%;
  margin: 3rem auto;
  border: 0;
  height: 4px;
  border-radius: 30px;
  background-color: var(--tes-cream);
  opacity: 0.7;
`;

const NuptialsImage = styled.img`
  width: 80%;
  max-width: 800px;
  height: auto;
  margin: 2rem auto;
  display: block;

  @media (max-width: 992px) {
    width: 95%;
  }

  @media (max-width: 768px) {
    width: 98%;
    margin: 1.5rem auto;
  }
`;

const AttireLineImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  margin: 0;
  padding: 0;
`;

const Section = styled.section`
  padding: 6rem 2rem 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: var(--tes-cream);
  margin-bottom: 0rem;
  padding-top: 5vh;

  @media (max-width: 768px) {
    padding: 5rem 1rem 3rem;
    padding-top: 5vh;
    margin-bottom: 0px;
  }
`;

const ScheduleSection = styled(Section)`
  background-image: url('/bg-green2.png');
  position: relative;
  padding-top: 0px;
  background-size: 100%;
  background-position: center top;
  background-repeat: repeat;
`;

const ScheduleTopLine = styled.hr`
  width: 100vw;
  height: 10px;
  background-color: var(--tes-cream);
  border: none;
  border-radius: 0px;
  margin-top: 1rem;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;

  @media (max-width: 768px) {
    height: 8px;
  }
`;

const TravelSection = styled(Section)`
  background-image: url('/scallop.png'), url('/bg-striped.png');
  background-position: center top, center top;
  background-size: 120%, cover;
  background-repeat: no-repeat, repeat-y;
  position: relative;
  padding-top: 0px;
  margin-top: 0;
  z-index: 10;
  & > * {
    position: relative;
    z-index: 2;
  }

  @media (max-width: 768px) {
    background-size: 300%, 120%;
    flex-direction: column;
    align-items: center;
  }
`;

const TravelContentContainer = styled.div`
  display: flex;
  font-family: var(--font-wingman) !important;
  width: 100%;
  max-width: 1200px;
  margin: 3rem auto;
  align-items: flex-start;

  @media (max-width: 992px) {
    flex-direction: column;
    align-items: center;
  }
`;

const TravelImageColumn = styled.div`
  flex: 1;
  padding: 1rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  @media (max-width: 992px) {
    width: 90%;
    max-width: 400px;
    padding: 0rem 0 0 0;
    margin-top: -2rem;
  }
  margin-bottom: 3rem;
`;

const TravelTextColumn = styled.div`
  flex: 1;
  padding: 1rem 4rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  max-width: 600px;

  & * {
    font-size: 1.4rem !important;
  }

  @media (max-width: 992px) {
    padding: 0.1rem 0.1rem;
    text-align: left;
    width: 90%;
    max-width: 600px;
    margin: -2rem auto;
  }
`;

const WelcomeImage = styled.img`
  max-width: 100%;
  height: auto;
  padding: 0px 0px 1rem 0px;
`;

const TravelText = styled.div`
  font-family: var(--font-primary);
  line-height: 1.6;

  h3 {
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    color: var(--tes-dark-blue);
  }

  p {
    margin-bottom: 1.2rem;
    font-size: 1.1rem;
  }

  strong {
    font-weight: bold;
  }

  a {
    color: var(--tes-green);
    text-decoration: underline;
    font-weight: bold;

    &:hover {
      color: var(--accent);
    }
  }
  padding: 0px 0px 1rem 0px;
`;

const Hero = styled(Section)`
  background-image: url('/bg-striped.png');
  background-size: cover;
  background-position: center top;
  background-repeat: repeat-y;
  color: var(--light);
  margin-top: 2rem;
  position: relative;
  overflow: hidden;
  padding-bottom: 50px;
  min-height: 50%;

 @media (max-width: 768px) {
    margin-top: 1rem;
    padding-bottom: 20px;
    min-height: 50%;
    background-size: 120%;
  }
`;
const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const InviteImage = styled.img`
  width: 70vw;
  max-width: 500px;
  min-width: 300px;
  height: auto;
  margin: 3rem 0rem 1.5rem 0;

  @media (max-width: 768px) {
    width: 70vw;
    max-width: 350px;
    min-width: 250px;
    margin: 1.5rem 0rem 1.5rem 0;
  }

  @media (max-width: 480px) {
    margin: 2rem 0rem 1.5rem 0;
    width: 80vw;
    min-width: 300px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-top: 8%;
  margin-bottom: 1rem;
  color: ${props => props.color || 'var(--tes-cream)'};

  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-top: 11%;
  }
`;

const AttireSection = styled.section`
  padding: 0;
  margin: 0;
  width: 100%;
  background-image: url('/brown tree extra tall.jpg');
  background-size: 125%;
  background-position: 20% 5%;
  background-repeat: repeat;
  height: auto;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;

  & > h2 {
    margin-bottom: 0rem;
    color: var(--tes-newcream);
  }

  @media (max-width: 1000px) {
    background-size: 200%;
    background-position: 6% 0%;
  }
`;

const AttireContentContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  margin: 3rem auto;
  align-items: flex-start;

  @media (max-width: 992px) {
    flex-direction: column;
    align-items: center;
    margin-top: 1rem;
    padding-top: 0rem;
    padding-left: 0;
  }
`;

const CocktailsImageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  padding: 2rem;
  padding-right: 4rem;

  @media (max-width: 992px) {
    width: 100%;
    padding: 0rem;
    justify-content: center;
    margin-top: 0rem;
  }
`;

const CocktailsImage = styled.img`
  max-width: 100%;
  height: auto;
  transform: rotate(-8deg);

  @media (max-width: 992px) {
    max-width: 80%;
  }
`;

const AttireTextColumn = styled.div`
  flex: 1;
  padding: 1rem 4rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  max-width: 600px;

  & * {
    font-size: 1.4rem !important;
  }

  @media (max-width: 992px) {
    padding: 0.1rem 0.1rem;
    text-align: left;
    width: 80%;
    max-width: 600px;
    margin-top: 0rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 576px) {
    margin-top: -1rem;
  }
`;

const AttireText = styled.div`
  font-family: var(--font-primary);
  line-height: 1.6;
  color: var(--tes-newcream) !important;

  p {
    margin-bottom: 1.2rem;
    font-size: 1.1rem;
  }

  strong {
    font-weight: bold;
  }

  padding: 0px 0px 1rem 0px;
`;

const GiftsTopLine = styled.hr`
  width: 100vw;
  height: 10px;
  background-color: var(--tes-dark-blue);
  border: none;
  border-radius: 0px;
  margin-top: 1rem;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;

  @media (max-width: 768px) {
    height: 8px;
  }
`;

const GiftsSection = styled(Section)`
  background-image: url('/bg-gifts2.png');
  color: var(--tes-dark-blue);
  position: relative;
  padding-top: 0px;
  background-size: 150%;
  background-position: center top;
  background-repeat: repeat;
`;

const GiftContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 20px;
  width: 100%;
  max-width: 1000px;
  margin: 2rem auto;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const GiftTextLeft = styled.div`
  font-family: var(--font-sweetheart);
  font-size: 1.3rem;
  color: var(--dark);
  text-align: right;
  flex: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const GiftTextRight = styled.div`
  font-family: var(--font-sweetheart);
  font-size: 1.3rem;
  color: var(--dark);
  text-align: left;
  flex: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const GiftImage = styled.div`
  flex: 0 0 auto;

  img {
    max-width: 300px;
    height: auto;
  }

  @media (max-width: 768px) {
    img {
      max-width: 200px;
    }
  }
`;

const GiftTextMobile = styled.div`
  display: none;
  font-family: var(--font-wingman);
  font-size: 1.3rem;
  color: var(--dark);
  text-align: center;
  width: 100%;
  margin-top: 20px;

  @media (max-width: 768px) {
    display: block;
  }
`;

const ContactSection = styled.div`
  background-image: url('/yellowfooter.png');
padding: 1.5rem;
  width: 100%;
  text-align: center;
  margin-top: -0rem;
  margin-bottom: 0rem;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  color: var(--tes-dark-blue);
`;

const ContactText = styled.p`
  font-family: var(--font-wingman);
  font-size: 1.2rem;
  color: var(--dark);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  background-color: var(--tes-green);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
    background-color: #ff3333;
  }

  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 1.2rem;
  }
`;

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [partyData, setPartyData] = useState(null);
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [rsvpResponses, setRsvpResponses] = useState({});
  const [declineAll, setDeclineAll] = useState(false);

  const initializePartyData = async (partyId) => {
    try {
      const response = await fetch(`https://api.shaneandtesily.com/api/party/${partyId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const updatedData = await response.json();
        localStorage.setItem('partyData', JSON.stringify(updatedData)); // Update localStorage
      } else {
        console.warn('Failed to refetch party data, using existing localStorage data');
      }
    } catch (error) {
      console.error('Error refetching party data:', error);
    }

    // After refetch (or if it fails), use the latest localStorage data
    const updatedPartyData = localStorage.getItem('partyData');
    if (updatedPartyData) {
      const refreshedData = JSON.parse(updatedPartyData);
      setPartyData(refreshedData);

      // Initialize RSVP responses
      const initialResponses = {};
      refreshedData.events.forEach(event => {
        if (refreshedData.rsvp && refreshedData.rsvp[event.name]) {
          initialResponses[event.name] = refreshedData.rsvp[event.name];
        } else {
          initialResponses[event.name] = 'yes';
        }
      });
      setRsvpResponses(initialResponses);
    }
  };

  useEffect(() => {
    if (showRsvpModal) {
      document.body.style.overflow = 'hidden'; // Disable scrolling
    } else {
      document.body.style.overflow = 'auto'; // Re-enable scrolling
    }
    // Cleanup to restore default behavior when component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showRsvpModal]);

  useEffect(() => {
    const savedPartyData = localStorage.getItem('partyData');
    const savedAuth = localStorage.getItem('weddingAuth');

    if (savedAuth === 'true' && savedPartyData) {
      try {
        const parsedData = JSON.parse(savedPartyData);
        setIsAuthenticated(true);
        if (parsedData.partyId) {
          initializePartyData(parsedData.partyId); // Refetch on page refresh
        } else {
          // No partyId, use existing localStorage data
          setPartyData(parsedData);
          const initialResponses = {};
          parsedData.events.forEach(event => {
            if (parsedData.rsvp && parsedData.rsvp[event.name]) {
              initialResponses[event.name] = parsedData.rsvp[event.name];
            } else {
              initialResponses[event.name] = 'yes';
            }
          });
          setRsvpResponses(initialResponses);

        }
      } catch (error) {
        console.error('Error parsing party data:', error);
        localStorage.removeItem('weddingAuth');
        localStorage.removeItem('partyData');
      }
    }
    const imagesToLoad = [
      '/navbar.png',
      '/bg-striped.png',
      '/scallop.png',
      '/invite.png',
      '/rsvp-btn.png',
      '/travel-welcome-to-san-diego.png',
      '/thursday.png',
    ];

    const startTime = Date.now();
    const MINIMUM_LOADING_TIME = 1000;
    let loadedCount = 0;
    const totalImages = imagesToLoad.length;

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = MINIMUM_LOADING_TIME - elapsedTime;
        setTimeout(() => setIsLoading(false), remainingTime > 0 ? remainingTime + 500 : 500);
      }
    };

    imagesToLoad.forEach((src) => {
      const img = new Image();
      img.onload = checkAllLoaded;
      img.onerror = checkAllLoaded;
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 768) setIsMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.nav-menu') && !event.target.closest('.menu-button')) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  const handleValidPassword = () => {
    setIsAuthenticated(true);
    const partyDataString = localStorage.getItem('partyData');
    if (partyDataString) {
      try {
        const parsedData = JSON.parse(partyDataString);
        if (parsedData.partyId) {
          initializePartyData(parsedData.partyId); // Refetch instead of setting state directly
        } else {
          // No partyId, use existing localStorage data
          setPartyData(parsedData);
          const initialResponses = {};
          parsedData.events.forEach(event => {
            if (parsedData.rsvp && parsedData.rsvp[event.name]) {
              initialResponses[event.name] = parsedData.rsvp[event.name];
            } else {
              initialResponses[event.name] = 'yes';
            }
          });
          setRsvpResponses(initialResponses);
       
        }
      } catch (error) {
        console.error('Error parsing party data:', error);
      }
    }
  };

  const handleRsvpClick = (e) => {
    e.preventDefault();
    setShowRsvpModal(true);
  };

  const handleRsvpSubmit = async (e) => {
    e.preventDefault();
    const partyId = JSON.parse(localStorage.getItem('partyData')).partyId || '';
    console.log('password:', partyId)
    try {
      const response = await fetch('https://api.shaneandtesily.com/api/rsvp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password:partyId, responses: rsvpResponses }),
      });

      if (response.ok) {
        setShowRsvpModal(false);
        alert('RSVP submitted successfully!');
      } else {
        throw new Error('Failed to submit RSVP');
      }
    } catch (error) {
      console.error('Error submitting RSVP:', error, partyId);
      alert('Error submitting RSVP. Please try again.');
    }
  };

  const handleResponseChange = (eventName, value) => {
    setRsvpResponses(prev => ({ ...prev, [eventName]: value }));
    setDeclineAll(false);
  };

  // Add a function to decline all events
  const handleDeclineAll = () => {
    const allDeclined = {};
    partyData?.events.forEach(event => {
      allDeclined[event.name] = 'no';
    });
    setRsvpResponses(allDeclined);
    setDeclineAll(true);
  };

  const renderPartyMembers = () => {
    if (!partyData || !partyData.members || partyData.members.length === 0) return { members: null, count: 0 };
    
    const validMembers = partyData.members.filter(member => member.firstName && member.firstName !== 'TBD');
    const memberCount = validMembers.length;
    
    const members = validMembers.map((member, index) => {
      const fullName = `${member.firstName} ${member.lastName || ''}`.trim();
      const isLast = index === validMembers.length - 1;
      const isSecondToLast = index === validMembers.length - 2;
      
      return (
        <span key={index}>
          {fullName}
          {isLast ? '' : (isSecondToLast ? ' & ' : ', ')}
        </span>
      );
    });
    
    return { members, count: memberCount };
  };

  return (
    <AppContainer>
      <LoadingScreen className={!isLoading ? 'loaded' : ''}>
        <LoadingText>Loading...</LoadingText>
      </LoadingScreen>

      {!isAuthenticated && <PasswordScreen onValidPassword={handleValidPassword} />}

      {isAuthenticated && (
        <>
          <NavBar>
  <NavBrand>T + S</NavBrand>
  <NavCenter>
    {windowWidth > 768 ? (
      <NavList>
        <NavItem><NavLink to="home" spy={true} smooth={true} duration={500} offset={-50}>HOME</NavLink></NavItem>
        <NavItem><NavLink to="travel" spy={true} smooth={true} duration={500} offset={-30}>TRAVEL</NavLink></NavItem>
        <NavItem><NavLink to="schedule" spy={true} smooth={true} duration={500} offset={-30}>SCHEDULE</NavLink></NavItem>
        <NavItem><NavLink to="attire" spy={true} smooth={true} duration={500} offset={-30}>ATTIRE</NavLink></NavItem>
        <NavItem><NavLink to="gifts" spy={true} smooth={true} duration={500} offset={-30}>GIFTS</NavLink></NavItem>
      </NavList>
    ) : (
      <>
        <MobileMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)} className="menu-button">
          MENU <MenuIcon isOpen={isMenuOpen} />
        </MobileMenuButton>
        <NavList isOpen={isMenuOpen} className="nav-menu">
          <NavItem>
            <NavLink
              to="home"
              smooth={true}
              duration={500}
              offset={-35}
              onClick={() => setIsMenuOpen(false)} // Close menu on click
            >
              HOME
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              to="travel"
              smooth={true}
              duration={500}
              offset={-35}
              onClick={() => setIsMenuOpen(false)} // Close menu on click
            >
              TRAVEL
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              to="schedule"
              smooth={true}
              duration={500}
              offset={-35}
              onClick={() => setIsMenuOpen(false)} // Close menu on click
            >
              SCHEDULE
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              to="attire"
              smooth={true}
              duration={500}
              offset={-35}
              onClick={() => setIsMenuOpen(false)} // Close menu on click
            >
              ATTIRE
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              to="gifts"
              smooth={true}
              duration={500}
              offset={-35}
              onClick={() => setIsMenuOpen(false)} // Close menu on click
            >
              GIFTS
            </NavLink>
          </NavItem>
        </NavList>
      </>
    )}
  </NavCenter>
  <NavDate>8.23.25</NavDate>
</NavBar>
          <Hero id="home">
            <HeroContent>
              <InviteImage src="/invite.png" alt="Wedding Invitation" />
              <RsvpButton onClick={handleRsvpClick}>
                <img src="/rsvp-btn.png" alt="RSVP Button" />
              </RsvpButton>
            </HeroContent>
          </Hero>

          <TravelSection id="travel">
            <SectionTitle color="var(--tes-dark-blue)">Travel</SectionTitle>
            <TravelContentContainer>
              <TravelImageColumn>
                <WelcomeImage src="/welcome-postcard.png" alt="Welcome to San Diego" />
              </TravelImageColumn>
              <TravelTextColumn>
                <TravelText>
                  <p><strong>WHILE THE CEREMONY & RECEPTION WILL TAKE PLACE IN VISTA, WE RECOMMEND THAT TRAVELLING GUESTS <u>STAY IN OCEANSIDE</u></strong> - THE LOCATION OF THE AFTERPARTY!</p>
                  <p>WE DON'T HAVE A HOTEL BLOCK, BUT HAVE MADE A LIST OF LOCAL HOTELS <a target="_blank" rel="noopener noreferrer" href="https://www.google.com/maps/d/edit?mid=1EjK9vc2Rq1_Dw5oyFroeDoVJMm4UZeM&usp=sharing">HERE.</a></p>
                  <p>A MAP OF RECOMMENDED ACTIVITIES & PLACES TO EAT IS <a target="_blank" rel="noopener noreferrer" href="https://www.google.com/maps/d/u/0/viewer?usp=sharing&mid=10SxIEYJhRnMMeOUQfeLfOKoMEIbrHMA">HERE.</a></p>
                </TravelText>
              </TravelTextColumn>
            </TravelContentContainer>
          </TravelSection>

          <ScheduleSection id="schedule">
            <ScheduleTopLine />
            <SectionTitle>SCHEDULE</SectionTitle>
            <ScheduleCardsContainer>
              <ScheduleCard><img src="/thursday.png" alt="Thursday Schedule" /></ScheduleCard>
              <ScheduleCard><img src="/friday.png" alt="Friday Schedule" /></ScheduleCard>
            </ScheduleCardsContainer>
            <ScheduleDivider />
            <NuptialsImage src={windowWidth <= 800 ? "/Wedding Website Elements Only_Mobile Nuptuals.png" : "/Wedding Website Elements Only_Desktop Nuptuals.png"} alt="Wedding Day Nuptials Schedule" />
          </ScheduleSection>

          <AttireLineImage src="/Wedding Website Attire line.png" alt="Attire Line Divider" />

          <AttireSection id="attire">
            <SectionTitle>ATTIRE</SectionTitle>
            <AttireContentContainer>
              <CocktailsImageContainer><CocktailsImage src="/cocktails.png" alt="Cocktail Attire" /></CocktailsImageContainer>
              <AttireTextColumn>
                <AttireText>
                  <p>The couple kindly requests that guests dress in cocktail attire for the celebration.</p>
                  <p>Light fabrics are encouraged, as late August in Vista is likely to be quite warm - around 80&deg;.</p>
                  <p>Please Note that the Rancho heavily features dirt, grass, & gravel, so flats or block heels are highly recommended.</p>
                </AttireText>
              </AttireTextColumn>
            </AttireContentContainer>
          </AttireSection>

          <GiftsSection id="gifts">
            <SectionTitle color="var(--tes-dark-blue)">GIFTS</SectionTitle>
            <GiftContainer>
              <GiftsTopLine />
              <GiftTextLeft>For info on gifting</GiftTextLeft>
              <GiftImage><img src="/endgif.gif" alt="Gift Information" /></GiftImage>
              <GiftTextRight>please click <a target="_blank" rel="noopener noreferrer" href="https://withjoy.com/shane-and-tesily/registry">here</a></GiftTextRight>
            </GiftContainer>
            <GiftTextMobile>for info on gifting please <a target="_blank" rel="noopener noreferrer" href="https://withjoy.com/shane-and-tesily/registry">click here</a></GiftTextMobile>
          </GiftsSection>

          <ContactSection>
            <ContactText>Please reach out to Tesily at 760.889.7056 with any questions</ContactText>
          </ContactSection>
        </>
      )}


{showRsvpModal && (
  <>
    <ModalOverlay onClick={() => setShowRsvpModal(false)} />
    <RsvpModal>
      <CloseButton onClick={() => setShowRsvpModal(false)}>X</CloseButton>
      <ModalTitle>RSVP for {renderPartyMembers().members}</ModalTitle>
      <ModalForm onSubmit={handleRsvpSubmit}>
      {partyData?.events
        .slice()
        .sort((a, b) => {
          const [monthA, dayA, yearA] = a.date.split('/').map(Number);
          const [monthB, dayB, yearB] = b.date.split('/').map(Number);
          const dateA = new Date(2000 + yearA, monthA - 1, dayA);
          const dateB = new Date(2000 + yearB, monthB - 1, dayB);
          if (dateA - dateB !== 0) return dateA - dateB;

          const timeA = a.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
          const timeB = b.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
          let hourA = parseInt(timeA[1], 10);
          const minuteA = parseInt(timeA[2], 10);
          const periodA = timeA[3].toUpperCase();
          let hourB = parseInt(timeB[1], 10);
          const minuteB = parseInt(timeB[2], 10);
          const periodB = timeB[3].toUpperCase();

          hourA = periodA === 'PM' && hourA !== 12 ? hourA + 12 : (periodA === 'AM' && hourA === 12 ? 0 : hourA);
          hourB = periodB === 'PM' && hourB !== 12 ? hourB + 12 : (periodB === 'AM' && hourB === 12 ? 0 : hourB);

          if (hourA !== hourB) return hourA - hourB;
          return minuteA - minuteB;
        })
        .map(event => {
          const { count } = renderPartyMembers();
          const isPlural = count > 1;
          
          return (
            <EventItem key={event.name}>
              <EventLabel>{`${event.name} - ${event.date} at ${event.time}`}</EventLabel>
              <p style={{ fontFamily: 'var(--font-wingman)', fontSize: '0.9rem', margin: '0' }}>{event.location}</p>
              <EventOptions>
                <EventButton 
                  type="button"
                  selected={rsvpResponses[event.name] === 'yes'}
                  onClick={() => handleResponseChange(event.name, 'yes')}
                >
                  {isPlural ? "We'll be there!" : "I'll be there!"}
                </EventButton>
                <EventButton 
                  type="button"
                  selected={rsvpResponses[event.name] === 'no'}
                  onClick={() => handleResponseChange(event.name, 'no')}
                >
                  {isPlural ? "We can't make it" : "I can't make it"}
                </EventButton>
              </EventOptions>
            </EventItem>
          );
        })}
        
        <CantAttendButton 
          type="button" 
          onClick={handleDeclineAll}
          selected={declineAll}
        >
          {renderPartyMembers().count > 1 
            ? "We're sorry, we can't make the wedding :/" 
            : "I'm sorry, I can't make the wedding :/"}
        </CantAttendButton>
        
        <SubmitButton 
          type="submit" 
        >
          Submit RSVP
        </SubmitButton>
      </ModalForm>
    </RsvpModal>
  </>
)}
    </AppContainer>
  );
}

export default App;